import React, { useState, useEffect } from 'react';
import { useAuth } from '../utils/AuthContext';
import api from '../utils/api';

const StarRating = ({ gameId, initialRating = null, onRatingChange, size = "md" }) => {
  const [rating, setRating] = useState(initialRating);
  const [hover, setHover] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { user, isAuthenticated } = useAuth();

  // Size classes for different star sizes
  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-5 h-5", 
    lg: "w-7 h-7"
  };

  useEffect(() => {
    if (isAuthenticated && user && gameId) {
      fetchUserRating();
    }
  }, [isAuthenticated, user, gameId]);

  const fetchUserRating = async () => {
    try {
      const response = await api.get(`/userratings/${gameId}`);
      setRating(response.data.rating);
    } catch (error) {
      console.error('Error fetching user rating:', error);
    }
  };

  const handleRatingClick = async (selectedRating) => {
    if (!isAuthenticated) {
      // Trigger login modal by dispatching a custom event
      window.dispatchEvent(new CustomEvent('openLoginModal'));
      return;
    }

    setIsLoading(true);
    try {
      if (rating === selectedRating) {
        // Remove rating if clicking the same star
        await api.delete(`/userratings/${gameId}`);
        setRating(null);
        if (onRatingChange) onRatingChange(null);
      } else {
        // Set new rating
        await api.post(`/userratings/${gameId}`, { rating: selectedRating });
        setRating(selectedRating);
        if (onRatingChange) onRatingChange(selectedRating);
      }
    } catch (error) {
      console.error('Error saving rating:', error);
      alert('Failed to save rating. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStar = (starNumber) => {
    const isFilled = (hover || rating) >= starNumber;
    
    return (
      <button
        key={starNumber}
        type="button"
        className={`${sizeClasses[size]} transition-colors duration-200 ${
          isFilled 
            ? 'text-yellow-400 hover:text-yellow-500' 
            : 'text-gray-300 hover:text-yellow-400'
        } ${isLoading ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        onClick={() => !isLoading && handleRatingClick(starNumber)}
        onMouseEnter={() => !isLoading && setHover(starNumber)}
        onMouseLeave={() => !isLoading && setHover(null)}
        disabled={isLoading}
        aria-label={`Rate ${starNumber} star${starNumber > 1 ? 's' : ''}`}
      >
        <svg
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      </button>
    );
  };

  return (
    <div className="flex items-center justify-center space-x-2">
      {[1, 2, 3, 4, 5].map(renderStar)}
      {isLoading && (
        <div className="ml-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
        </div>
      )}
    </div>
  );
};

export default StarRating; 